import { useEffect, useRef } from "react";
import { sampleWind, makeWindConfig } from "./windField";

/**
 * PaperSun — a flat cut-paper sun whose rays sway on the SAME wind field that
 * blows BlowingPetals' leaves. Faceless, idiom-true (no gradients): a marigold
 * paper disc with a ring of paper rays, each ray a torsional spring-damper
 * hinged at the rim and driven by the shared simplex/gust wind. The disc itself
 * bobs on a light 2-DOF spring and leans a hair into the prevailing gust.
 *
 * The "skin" is deliberately simple; the "bones" are real: N second-order ray
 * oscillators + a 2-DOF disc spring, integrated each frame against the wind
 * field. Cost is ~rayCount wind samples per frame — trivial.
 *
 * Perf/gating mirrors BlowingPetals: caps DPR, pauses on prefers-reduced-motion
 * / off-screen / backgrounded tab, and always paints a static rest pose so the
 * sun is present even while the loop is parked.
 *
 * @param {number} size         — square canvas size in px (disc + rays + shadow fit inside)
 * @param {string} tone         — base paper colour; accepts a hex/rgb or a "var(--x)" token
 * @param {number} rayCount     — number of rays around the disc
 * @param {number} windStrength — 0.1–1.0; pass the same value as the petals to couple them
 * @param {number} liveliness   — 0.5–1.5; scales ray softness + sway amplitude
 * @param {object} windAnchorRef — ref to the element that owns the shared wind
 *   field's coordinate space (e.g. the hero band that also hosts BlowingPetals).
 *   When provided, the sun samples the wind at its TRUE position within that
 *   element instead of in its own local canvas space, so the gust that sweeps the
 *   petals reaches the sun in phase. Omit it to sample standalone (local space).
 * @param {string} className
 * @param {object} style
 */
export default function PaperSun({
  size = 120,
  fill = false,
  tone = "var(--color-secondary)",
  rayCount = 12,
  windStrength = 0.6,
  liveliness = 1,
  windAnchorRef,
  className = "",
  style: externalStyle,
}) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const rafRef = useRef(null);
  const safetyTimeoutRef = useRef(null);
  const lastFrameRef = useRef(0);
  const timeRef = useRef(0);
  const dprRef = useRef(1);

  const reducedMotionRef = useRef(false);
  const isIntersectingRef = useRef(true);
  const tabVisibleRef = useRef(true);
  const startLoopRef = useRef(null);
  const drawRestRef = useRef(null);

  // Geometry + physics state, (re)built on mount/resize.
  const rigRef = useRef(null);
  const colorsRef = useRef(null);

  // Keep tuning props on refs so the rAF loop reads live values without restart.
  const windStrengthRef = useRef(windStrength);
  const livelinessRef = useRef(liveliness);
  const rayCountRef = useRef(rayCount);
  const toneRef = useRef(tone);
  useEffect(() => { windStrengthRef.current = windStrength; }, [windStrength]);
  useEffect(() => { livelinessRef.current = liveliness; }, [liveliness]);

  // The sun's place within the shared wind field: its canvas offset inside the
  // anchor element (CSS px) and the anchor's width (the petals' containerWidth).
  // Measured from layout so wind samples land at the sun's true scene position.
  const windAnchorElRef = useRef(null);
  const windOffsetRef = useRef({ x: 0, y: 0 });
  const windContainerWidthRef = useRef(0);
  useEffect(() => { windAnchorElRef.current = windAnchorRef?.current || null; }, [windAnchorRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // ── Colour resolution ────────────────────────────────────────────────
    // Resolve the base paper colour (a hex, an rgb(), or a "var(--token)") to
    // concrete RGB, then derive a lighter ray paper + a darker fold accent by
    // flat mixing — no gradients, just three solid cut-paper tones.
    const resolveColors = () => {
      const base = toRGB(resolveColor(toneRef.current, canvas)) || { r: 242, g: 169, b: 59 };
      const inkVar = getComputedStyle(canvas).getPropertyValue("--paper-ink-rgb").trim();
      colorsRef.current = {
        disc: rgb(base),
        ray: rgb(shade(base, 0.16)),       // butter — rays read as a layer behind the disc
        fold: rgb(shade(base, -0.3)),      // amber crease/fold accent
        ink: inkVar || "58,46,38",
      };
    };

    // ── Rig (geometry + per-ray physics) ─────────────────────────────────
    // Deterministic "hand-cut" jitter is baked once here so nothing shimmers
    // frame to frame.
    const buildRig = (cssSize) => {
      const n = Math.max(4, Math.round(rayCountRef.current));
      const cx = cssSize / 2;
      const cy = cssSize / 2;
      // Leave room for the longest ray + disc bob + the paper-lift shadow.
      const discR = cssSize * 0.235;
      const rayLen = cssSize * 0.15;
      const rays = [];
      let seed = 0.123 + n * 0.07;
      const rnd = () => { seed = (seed * 9301 + 0.49297) % 1; return seed; };
      for (let i = 0; i < n; i++) {
        rays.push({
          base: (i / n) * Math.PI * 2 + (rnd() - 0.5) * 0.08, // hand-placed angle jitter
          len: rayLen * (0.82 + rnd() * 0.36),                 // organic length variation
          halfW: discR * (0.16 + rnd() * 0.05),
          phi: 0,    // angular deviation from rest (radial)
          omega: 0,  // angular velocity
        });
      }
      // Hand-cut disc outline: a many-sided polygon with tiny radius jitter.
      const sides = 30;
      const verts = [];
      for (let i = 0; i < sides; i++) {
        verts.push(discR * (1 + (rnd() - 0.5) * 0.035));
      }
      rigRef.current = { cx, cy, discR, sides, verts, rays, dx: 0, dy: 0, vx: 0, vy: 0 };
    };

    // ── Physics step ─────────────────────────────────────────────────────
    const RAY_INERTIA = 0.9;     // resistance to angular acceleration
    const RAY_STIFFNESS = 16;    // spring back toward radial rest
    const RAY_DAMPING = 1.7;     // angular damping
    const RAY_LEVER = 0.012;     // how strongly perpendicular wind torques a ray
    const RAY_MAX = 0.5;         // paper won't bend past ~28°
    const DISC_PUSH = 0.012;     // wind -> disc translation force
    const DISC_K = 26;           // disc spring
    const DISC_C = 6;            // disc damping
    const DISC_MAX = 5;          // px of bob travel

    const step = (dt, time) => {
      const rig = rigRef.current;
      if (!rig) return;
      const cfg = makeWindConfig(windStrengthRef.current);
      const live = livelinessRef.current;
      const k = RAY_STIFFNESS / live; // livelier => softer spring => bigger sway
      const cx = rig.cx + rig.dx;
      const cy = rig.cy + rig.dy;
      // Map local canvas coords -> shared wind-field coords (the sun's true spot
      // in the scene) so the same gust hits the sun and the nearby petals alike.
      const off = windOffsetRef.current;
      const cw = windContainerWidthRef.current || rig.cx * 2;

      let phiSum = 0;
      for (let i = 0; i < rig.rays.length; i++) {
        const ray = rig.rays[i];
        const dir = ray.base + ray.phi;
        const dxr = Math.cos(dir), dyr = Math.sin(dir);
        // Sample wind at the ray tip so rays across the disc flutter independently.
        const tipx = cx + dxr * (rig.discR + ray.len);
        const tipy = cy + dyr * (rig.discR + ray.len);
        const w = sampleWind(off.x + tipx, off.y + tipy, time, cfg, cw);
        // Torque from the wind component perpendicular to the ray (cross product).
        const cross = dxr * w.y - dyr * w.x;
        const torque = cross * RAY_LEVER * live - k * ray.phi - RAY_DAMPING * ray.omega;
        ray.omega += (torque / RAY_INERTIA) * dt;
        ray.phi += ray.omega * dt;
        if (ray.phi > RAY_MAX) { ray.phi = RAY_MAX; if (ray.omega > 0) ray.omega *= -0.25; }
        else if (ray.phi < -RAY_MAX) { ray.phi = -RAY_MAX; if (ray.omega < 0) ray.omega *= -0.25; }
        phiSum += ray.phi;
      }

      // Disc bob: a light 2-DOF spring nudged by the wind at the centre.
      const wc = sampleWind(off.x + rig.cx, off.y + rig.cy, time, cfg, cw);
      const ax = wc.x * DISC_PUSH * live - DISC_K * rig.dx - DISC_C * rig.vx;
      const ay = wc.y * DISC_PUSH * live - DISC_K * rig.dy - DISC_C * rig.vy;
      rig.vx += ax * dt; rig.vy += ay * dt;
      rig.dx += rig.vx * dt; rig.dy += rig.vy * dt;
      rig.dx = clamp(rig.dx, -DISC_MAX, DISC_MAX);
      rig.dy = clamp(rig.dy, -DISC_MAX, DISC_MAX);

      // The whole sun leans a hair the way the rays are combing.
      rig.lean = (phiSum / rig.rays.length) * 0.18;
    };

    // ── Render ───────────────────────────────────────────────────────────
    const draw = () => {
      const ctx = ctxRef.current;
      const rig = rigRef.current;
      const col = colorsRef.current;
      if (!ctx || !rig || !col) return;
      const dpr = dprRef.current;

      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.translate(rig.cx + rig.dx, rig.cy + rig.dy);
      ctx.rotate(rig.lean || 0);

      // Paper-lift shadow: crisp, low blur, warm ink (matches the petals/theme).
      ctx.shadowColor = `rgba(${col.ink},0.18)`;
      ctx.shadowBlur = 3;
      ctx.shadowOffsetX = 1.5;
      ctx.shadowOffsetY = 2.5;

      // Rays first, behind the disc.
      ctx.fillStyle = col.ray;
      for (let i = 0; i < rig.rays.length; i++) {
        const ray = rig.rays[i];
        ctx.save();
        ctx.rotate(ray.base + ray.phi);
        const r0 = rig.discR * 0.9;
        const r1 = rig.discR + ray.len;
        const hw = ray.halfW;
        ctx.beginPath();
        ctx.moveTo(r0, -hw);
        ctx.quadraticCurveTo((r0 + r1) * 0.5, -hw * 0.45, r1, 0); // taper to a soft point
        ctx.quadraticCurveTo((r0 + r1) * 0.5, hw * 0.45, r0, hw);
        ctx.closePath();
        ctx.fillStyle = col.ray;
        ctx.fill();
        // Fold crease down one edge (darker paper at low alpha) — no gradient.
        ctx.save();
        ctx.globalAlpha = 0.28;
        ctx.fillStyle = col.fold;
        ctx.beginPath();
        ctx.moveTo(r0, hw);
        ctx.quadraticCurveTo((r0 + r1) * 0.5, hw * 0.45, r1, 0);
        ctx.lineTo(r1, 0);
        ctx.quadraticCurveTo((r0 + r1) * 0.5, hw * 0.18, r0, hw * 0.45);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        ctx.restore();
      }

      // Disc: a flat hand-cut paper circle.
      ctx.beginPath();
      for (let i = 0; i < rig.sides; i++) {
        const a = (i / rig.sides) * Math.PI * 2;
        const r = rig.verts[i];
        const x = Math.cos(a) * r, y = Math.sin(a) * r;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fillStyle = col.disc;
      ctx.fill();

      // Fold accent: a thin darker arc hugging the lower-left rim — gives the
      // "two layers of cut paper" depth without a radial gradient. Drawn after
      // the disc fill and clipped to it.
      ctx.save();
      ctx.clip();
      ctx.globalAlpha = 0.16;
      ctx.strokeStyle = col.fold;
      ctx.lineWidth = rig.discR * 0.5;
      ctx.beginPath();
      ctx.arc(0, 0, rig.discR * 0.9, Math.PI * 0.55, Math.PI * 1.15);
      ctx.stroke();
      ctx.restore();

      ctx.restore();
    };

    drawRestRef.current = () => {
      // Settle the rig to rest, then paint a single static frame.
      const rig = rigRef.current;
      if (rig) {
        for (const ray of rig.rays) { ray.phi = 0; ray.omega = 0; }
        rig.dx = rig.dy = rig.vx = rig.vy = 0; rig.lean = 0;
      }
      draw();
    };

    // ── Loop ─────────────────────────────────────────────────────────────
    const animate = (timestamp) => {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
      if (reducedMotionRef.current || !isIntersectingRef.current || !tabVisibleRef.current) {
        rafRef.current = null;
        drawRestRef.current?.(); // park on a clean static pose
        if (!safetyTimeoutRef.current) {
          safetyTimeoutRef.current = setTimeout(() => {
            safetyTimeoutRef.current = null;
            startLoopRef.current?.();
          }, 1000);
        }
        return;
      }
      const dt = Math.min((timestamp - lastFrameRef.current) / 1000, 0.05);
      lastFrameRef.current = timestamp;
      timeRef.current += dt;
      step(dt, timeRef.current);
      draw();
      rafRef.current = requestAnimationFrame(animate);
    };

    startLoopRef.current = () => {
      if (rafRef.current) return;
      if (reducedMotionRef.current || !isIntersectingRef.current || !tabVisibleRef.current) return;
      lastFrameRef.current = performance.now();
      rafRef.current = requestAnimationFrame(animate);
    };

    // ── Setup ────────────────────────────────────────────────────────────
    // Locate the sun within the shared wind field: the offset of its canvas
    // inside the anchor element, and the anchor's width (the wind containerWidth
    // the petals use). The offset is scroll-invariant — both rects move together
    // — so it only needs refreshing on layout/resize. Falls back to local space
    // when no anchor is given, preserving standalone use.
    const measureAnchor = () => {
      const anchorEl = windAnchorElRef.current;
      if (anchorEl) {
        const a = anchorEl.getBoundingClientRect();
        const c = canvas.getBoundingClientRect();
        windOffsetRef.current.x = c.left - a.left;
        windOffsetRef.current.y = c.top - a.top;
        windContainerWidthRef.current = a.width || canvas.clientWidth || 0;
      } else {
        windOffsetRef.current.x = 0;
        windOffsetRef.current.y = 0;
        windContainerWidthRef.current =
          canvas.clientWidth || (typeof size === "number" ? size : 150);
      }
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      dprRef.current = dpr;
      const cssSize = canvas.clientWidth || (typeof size === "number" ? size : 150);
      canvas.width = Math.round(cssSize * dpr);
      canvas.height = Math.round(cssSize * dpr);
      resolveColors();
      buildRig(cssSize);
      measureAnchor();
      // Always paint something immediately so the sun is visible even if the
      // loop is currently parked (off-screen / reduced motion).
      drawRestRef.current?.();
    };

    ctxRef.current = canvas.getContext("2d");
    resize();

    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedMotionRef.current = mql.matches;
    const onMotion = (e) => {
      reducedMotionRef.current = e.matches;
      if (e.matches) drawRestRef.current?.();
      else startLoopRef.current?.();
    };
    mql.addEventListener("change", onMotion);

    // Observe the canvas (its own size, at breakpoints) and the anchor (the hero
    // width drives the sun's % offset + the wind containerWidth). A canvas change
    // does a full resize (which re-measures); an anchor-only change just remeasures
    // the offset, avoiding a needless rig rebuild on every horizontal resize.
    const ro = new ResizeObserver((entries) => {
      let needResize = false;
      for (const e of entries) {
        if (e.target === canvas) needResize = true;
      }
      if (needResize) resize();
      else measureAnchor();
    });
    ro.observe(canvas);
    if (windAnchorElRef.current) ro.observe(windAnchorElRef.current);

    const io = new IntersectionObserver(
      ([entry]) => {
        isIntersectingRef.current = entry.isIntersecting;
        if (entry.isIntersecting) startLoopRef.current?.();
      },
      { threshold: 0 }
    );
    io.observe(canvas);

    const onVisibility = () => {
      tabVisibleRef.current = document.visibilityState === "visible";
      if (tabVisibleRef.current) startLoopRef.current?.();
    };
    document.addEventListener("visibilitychange", onVisibility);

    lastFrameRef.current = performance.now();
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      mql.removeEventListener("change", onMotion);
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
    };
    // size is a layout input; ResizeObserver handles live changes, but a new
    // size prop should rebuild from scratch.
  }, [size]);

  return (
    <div
      className={className}
      aria-hidden="true"
      style={{
        ...(fill ? { width: "100%", height: "100%" } : { width: size, height: size }),
        ...externalStyle,
      }}
    >
      <canvas
        ref={canvasRef}
        role="presentation"
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </div>
  );
}

// ─── Colour helpers ─────────────────────────────────────────────────────────
function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

function resolveColor(tone, el) {
  const v = String(tone).trim();
  const m = v.match(/^var\((--[\w-]+)\)$/);
  if (m) return getComputedStyle(el).getPropertyValue(m[1]).trim();
  return v;
}

function toRGB(str) {
  if (!str) return null;
  const s = str.trim();
  let m = s.match(/^#([0-9a-f]{3})$/i);
  if (m) {
    const h = m[1];
    return { r: parseInt(h[0] + h[0], 16), g: parseInt(h[1] + h[1], 16), b: parseInt(h[2] + h[2], 16) };
  }
  m = s.match(/^#([0-9a-f]{6})$/i);
  if (m) {
    const h = m[1];
    return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
  }
  m = s.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
  if (m) return { r: +m[1], g: +m[2], b: +m[3] };
  return null;
}

// Flat-mix a colour toward white (amt>0) or black (amt<0); returns {r,g,b}.
function shade(c, amt) {
  const t = amt < 0 ? 0 : 255;
  const p = Math.abs(amt);
  return {
    r: Math.round((t - c.r) * p) + c.r,
    g: Math.round((t - c.g) * p) + c.g,
    b: Math.round((t - c.b) * p) + c.b,
  };
}

function rgb(c) { return `rgb(${c.r},${c.g},${c.b})`; }
